import {registerTemplateGraphNode} from "#template/template-node/register"
import {pointGeometryAttributeSetLike, pointGeometryAttributeSetLikeMarker} from "#template/nodes/geometry-graph/attribute-set/attribute-set-node"
import {pointGeometryAttributeLike} from "#template/nodes/geometry-graph/attribute/attribute-node"
import type {PointGeometryAttributeLikeNode} from "#template/nodes/geometry-graph/attribute/attribute-node"
import type {PointGeometryAttributeSetLikeNode} from "#template/nodes/geometry-graph/attribute-set/attribute-set-node"

import type {TemplateNodeDefinition} from "#template/template-node/types"
import {getTemplateNodeReturns} from "#template/template-node/validation"

import type {IModifyTemplateGraph} from "#template/interactions"
import type {AttributeSetRecordInputParameters} from "#template/nodes/geometry-graph/attribute-set/attribute-set-record-input/geometry-attribute-set-record-input-node"
import {
    GeometryAttributeSetRecordInputNode,
    attributeSetRecordInputParametersSchema,
} from "#template/nodes/geometry-graph/attribute-set/attribute-set-record-input/geometry-attribute-set-record-input-node"

export type PointGeometryAttributeSetRecordInputParameters = AttributeSetRecordInputParameters<
    PointGeometryAttributeLikeNode,
    PointGeometryAttributeSetLikeNode
>

export interface ModifyPointGeometryAttributeSetRecordInputLikeNode {
    addRecordEntry(
        key: string,
        value: PointGeometryAttributeLikeNode | PointGeometryAttributeSetLikeNode | null,
        modifyTemplateGraph: IModifyTemplateGraph,
    ): void
    removeRecordEntry(key: string, modifyTemplateGraph: IModifyTemplateGraph): void
    renameRecordEntry(oldKey: string, newKey: string, modifyTemplateGraph: IModifyTemplateGraph): void
}

const definition: TemplateNodeDefinition<PointGeometryAttributeSetRecordInputParameters> = {
    nodeClass: "PointGeometryAttributeSetRecordInput",
    validation: {
        parameters: attributeSetRecordInputParametersSchema(pointGeometryAttributeLike, pointGeometryAttributeSetLike),
        returns: getTemplateNodeReturns(),
    },
}

@registerTemplateGraphNode
export class PointGeometryAttributeSetRecordInput
    extends GeometryAttributeSetRecordInputNode<0, PointGeometryAttributeLikeNode, PointGeometryAttributeSetLikeNode>
    implements PointGeometryAttributeSetLikeNode, ModifyPointGeometryAttributeSetRecordInputLikeNode
{
    get [pointGeometryAttributeSetLikeMarker]() {
        return true as const
    }

    public override get definition() {
        return definition
    }

    protected override get outputIndexingDim(): 0 {
        return 0
    }
    addRecordEntry(
        key: string,
        value: PointGeometryAttributeLikeNode | PointGeometryAttributeSetLikeNode | null,
        modifyTemplateGraph: IModifyTemplateGraph,
    ): void {
        modifyTemplateGraph.modifyTemplateGraph(() => this.updateParameters({record: {...this.parameters.record, [key]: value}}))
    }

    removeRecordEntry(key: string, modifyTemplateGraph: IModifyTemplateGraph): void {
        modifyTemplateGraph.modifyTemplateGraph(() => {
            const newRecord = {...this.parameters.record}
            delete newRecord[key]
            this.updateParameters({record: newRecord})
        })
    }

    renameRecordEntry(oldKey: string, newKey: string, modifyTemplateGraph: IModifyTemplateGraph): void {
        modifyTemplateGraph.modifyTemplateGraph(() => {
            const newRecord = {...this.parameters.record}
            if (newRecord[oldKey] === undefined) throw new Error(`Key "${oldKey}" does not exist in record`)

            newRecord[newKey] = newRecord[oldKey]
            delete newRecord[oldKey]
            this.updateParameters({record: newRecord})
        })
    }
}
